import React, { useState } from 'react';

/**
 * HeatLevelIndicator Component
 *
 * Displays and interacts with 5-star heat level system for lead quality scoring.
 *
 * @param {number|null} level - Current heat level (1-5 or null for unrated)
 * @param {function} onChange - Callback when heat level changes (value) => void
 * @param {boolean} editable - Whether the heat level can be changed (default: true)
 * @param {string} size - Size variant: 'sm', 'md', 'lg' (default: 'md')
 */
function HeatLevelIndicator({ level, onChange, editable = true, size = 'md' }) {
  const [hoverLevel, setHoverLevel] = useState(0);

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const handleClick = (value) => {
    if (editable && onChange) {
      // If clicking the same level, clear it (set to null)
      onChange(value === level ? null : value);
    }
  };

  const handleMouseEnter = (value) => {
    if (editable) {
      setHoverLevel(value);
    }
  };

  const handleMouseLeave = () => {
    setHoverLevel(0);
  };

  // Determine which stars should be filled
  const displayLevel = hoverLevel || level || 0;

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = displayLevel >= star;
        const isHovering = editable && hoverLevel === star;

        return (
          <button
            key={star}
            type="button"
            onClick={() => handleClick(star)}
            onMouseEnter={() => handleMouseEnter(star)}
            onMouseLeave={handleMouseLeave}
            disabled={!editable}
            className={`
              ${editable ? 'cursor-pointer hover:scale-110' : 'cursor-default'}
              transition-transform duration-150
              focus:outline-none
              ${isHovering ? 'scale-110' : ''}
            `}
            aria-label={`Rate ${star} stars`}
          >
            <svg
              className={`
                ${sizeClasses[size]}
                ${filled ? 'text-tenq-gold' : 'text-gray-300'}
                transition-colors duration-150
                ${filled ? 'drop-shadow-sm' : ''}
              `}
              fill={filled ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth={filled ? 0 : 2}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          </button>
        );
      })}
    </div>
  );
}

export default HeatLevelIndicator;
